import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import ItemInfoForm from './ItemInfoForm';
import Map from '../molecules/MapBox';
import WebLink from '../atoms/WebLink';
import Button from '../atoms/Button';
import Property from '../atoms/Property';
import { toAddressBlock } from '../../lib/utils';
import {
  display,
  contact_details_fields,
  social_media_fields,
} from '../../data/display';
import styles from '../../styles/components/organisms/ItemInfo.module.scss';

interface ItemInfoProps {
  venueID?: string;
  serviceProviderID?: string;
  data: any;
  access?: boolean;
  apply: (id: string, data?: any) => void;
}

const ItemInfo = (props: ItemInfoProps) => {
  const { t } = useTranslation(['common']);
  const { venueID, serviceProviderID, data, access, apply } = props;
  const [edit, setEdit] = useState(false);
  const type = !!venueID ? 'venue' : 'service';
  const planData = display.venue.platinum as any;

  /*
   * Intro / Header Data
   */

  // Get fields to display
  const displayObj = planData.intro;
  const contactDetailsFields = Object.keys(contact_details_fields);
  const socialMediaFields = Object.keys(social_media_fields);
  const contactFields = [...contactDetailsFields, ...socialMediaFields];

  // Properties
  const propertyKeys = Object.keys(displayObj);
  const properties = {} as any;
  propertyKeys.forEach((key: string) => {
    const id = key;
    const defaults = displayObj[id];
    const value = data[id] || (defaults ? defaults.value : '');
    //  console.log(key, value)
    properties[id] = value;
  });

  const { headline, description, lat, lng } = properties as any;
  const addressBlock = toAddressBlock(data);

  return (
    <>
      {edit ? (
        <ItemInfoForm
          venueID={venueID}
          serviceProviderID={serviceProviderID}
          prefill={data}
          onSubmit={() => setEdit(false)}
          onCancel={() => setEdit(false)}
        />
      ) : (
        <Row className={styles.itemInfo}>
          {!!headline && (
            <Col xs={8} md={5}>
              <h2>{headline}</h2>
            </Col>
          )}

          <Col xs={8} md={4}>
            <p>{description}</p>
          </Col>

          {!!venueID ? (
            <>
              {/* Venue */}
              {type === 'venue' && (
                <Col xs={8} md={2} className={styles.mapColumn}>
                  <br />
                  <Map id="headerMap" type={type} data={{ lat, lng }} />
                </Col>
              )}

              <Col xs={8} md={2} className={styles.addressColumn}>
                <br />
                <Property dark icon="location" label={t('common:address')} />
                <p>{addressBlock}</p>
                <p>
                  {contactFields
                    .filter((el) => !!properties[el])
                    .map((el, i) => (
                      <WebLink
                        href={properties[el]}
                        label={el === 'website' ? t('common:website') : ''}
                        key={i}
                        icon={el}
                      />
                    ))}
                </p>
              </Col>
            </>
          ) : (
            <>
              {/* Service Provider */}
              <Col xs={0} md={1} className={styles.addressColumn}></Col>
              <Col xs={5} md={2} className={styles.addressColumn}>
                <br />
                <Property dark icon="location" label={t('common:address')} />
                <p>{addressBlock}</p>
                <br />
              </Col>

              <Col xs={3} md={1} className={styles.addressColumn}>
                <br />
                <Property dark icon="contact" label={t('common:contact')} />
                <p>
                  {contactFields
                    .filter((el) => !!properties[el])
                    .map((el, i) => (
                      <WebLink
                        href={properties[el]}
                        label={el === 'website' ? t('common:website') : ''}
                        key={i}
                        icon={el}
                      />
                    ))}
                </p>
                <br />
              </Col>
            </>
          )}

          {!!access && (
            <Col xs={8}>
              <Button
                small
                dark
                icon={'edit'}
                label={t(`common:cta-edit`)}
                onClick={() => setEdit(true)}
              />
            </Col>
          )}
        </Row>
      )}
    </>
  );
};

export default ItemInfo;
